// Дребните парчета на „теоретично срещу реално" в „Продадени продукти":
// разликата със знак (к-во и пари — кехлибарена, когато я има, тире при нула,
// „…" докато реалното се зарежда), значката „⇄" за замяна в реда и редът с
// подсказката кое число по коя дата се брои. Ползват ги таблицата, картите,
// тесният списък и лентата над складовата карта.
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  formatSignedMoney,
  formatSignedQuantity,
  hasSubstitution,
  substitutionLines,
  type SoldActual,
} from "./soldVarianceModel";

const PENDING = <span className="text-faint">…</span>;
const DASH = <span className="text-faint">—</span>;

// Разликата в количество; `value` null — реалното още не е дошло
export const SignedQuantity = ({
  value,
  decimals,
  unit,
}: {
  value: number | null;
  decimals: number;
  unit?: string | null;
}) => {
  if (value == null) return PENDING;
  const text = formatSignedQuantity(value, decimals);
  if (!text) return DASH;
  return (
    <span className="whitespace-nowrap font-sheet font-semibold tabular-nums text-warn-ink">
      {text}
      {unit ? <span className="font-normal text-muted"> {unit}</span> : null}
    </span>
  );
};

export const SignedMoney = ({ value, strong = false }: { value: number | null; strong?: boolean }) => {
  if (value == null) return PENDING;
  const text = formatSignedMoney(value);
  if (!text) return DASH;
  return (
    <span className={`whitespace-nowrap font-sheet tabular-nums text-warn-ink ${strong ? "font-bold" : "font-semibold"}`}>
      {text}
    </span>
  );
};

// „⇄" до името: при наличие на замяна в разнасянията на периода
export const SubstitutionMark = ({
  actual,
  decimals,
  unit,
}: {
  actual: SoldActual | null;
  decimals: number;
  unit?: string | null;
}) => {
  const { t } = useTranslation();
  if (!hasSubstitution(actual)) return null;
  const lines = substitutionLines(actual, decimals, unit, t);
  return (
    <Tooltip
      title={
        <div className="flex flex-col gap-0.5 text-[12.5px] leading-[1.4]">
          {lines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>
      }
    >
      <span
        aria-label={lines.join(". ")}
        className="inline-flex shrink-0 items-center rounded-full bg-brand-tint px-1.5 py-[1px] text-[11.5px] font-medium leading-[normal] text-brand-deep-ink"
      >
        ⇄
      </span>
    </Tooltip>
  );
};

// Кое число по коя дата: теоретичното — по затварянето на сметките,
// реалното — по датата на отчета. Подсказката изрежда причините за разлика.
export const SoldDatesHint = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5 border-b border-shade-1 px-[18px] py-2 text-[12.5px] leading-[1.4] text-body">
      <Tooltip title={<span className="text-[12.5px] leading-[1.4]">{t("inventoryPage.sdDatesHintTip")}</span>}>
        {/* Фокусируем — подсказката се чете и от клавиатура/екранен четец */}
        <span
          tabIndex={0}
          role="img"
          aria-label={t("inventoryPage.sdDatesHintTip")}
          className="inline-flex shrink-0 rounded-full text-muted outline-none focus-visible:shadow-[0_0_0_2px_var(--color-brand)]"
        >
          <InfoCircleOutlined aria-hidden />
        </span>
      </Tooltip>
      <span>{t("inventoryPage.sdDatesHint")}</span>
    </div>
  );
};
